import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * useGamePreviewHandlers - Unified handlers for GamePreviewModal actions
 *
 * Provides consistent behavior across Collection and Simplify contexts for:
 * - Search similar (opens Curate sidebar with game chip)
 * - Find a copy (opens Marketplace)
 * - Add reminder
 * - Watch
 * - Let's Play
 *
 * @param {Object} config - Configuration object
 * @param {Function} config.setPreviewGame - Close preview modal
 * @param {Function} config.setShowCurateSidebar - Show Curate sidebar
 * @param {Function} config.setMarketplaceGame - Set marketplace game (renders Marketplace)
 * @param {Function} config.setReminderGame - Set reminder game
 * @param {Object} config.curateBridge - Curate bridge methods { showSimilarGames, showComparison, resetCurate }
 * @param {Function} [config.showBanner] - Optional banner notification
 * @param {Function} [config.setWatchGame] - Optional: for Watch feature
 * @param {Function} [config.setLetSPlayGame] - Optional: for Let's Play feature
 * @param {Function} [config.setShowLetSPlayModal] - Optional: for Let's Play feature
 */
export function useGamePreviewHandlers({
  setPreviewGame,
  setShowCurateSidebar,
  setMarketplaceGame,
  setReminderGame,
  curateBridge,
  showBanner,
  setWatchGame,
  setLetSPlayGame,
  setShowLetSPlayModal,
}) {
  const navigate = useNavigate();

  // Handler for "Search similar" - navigate to the dedicated Curate URL.
  const handleSearchSimilar = useCallback((game) => {
    if (!game) return;

    // Close any preview modal that triggered this action.
    setPreviewGame?.(null);

    const gameId = game.id ?? game.game_id;
    navigate(`/curate?gameId=${gameId}&weight=0.5&scope=all`);
  }, [setPreviewGame, navigate]);

  // Handler for "Find a copy" (marketplace)
  const handleAddToMarketplace = useCallback((game) => {
    if (!game) return;

    // Set marketplace game (renders Marketplace component)
    setMarketplaceGame({ id: game.game_id || game.id, name: game.name });

    showBanner?.(`Opening marketplace for ${game.name}...`, 'info');
  }, [setMarketplaceGame, showBanner]);

  // Handler for "Watch"
  const handleAddWatch = useCallback((game) => {
    if (!game) return;

    if (setWatchGame) {
      setWatchGame(game);
    }

    showBanner?.('Watch feature coming soon!', 'info');
  }, [setWatchGame, showBanner]);

  // Handler for "Reminder"
  const handleAddReminder = useCallback((game) => {
    if (!game) return;

    // Set reminder game (renders reminder modal)
    setReminderGame({ id: game.game_id || game.id, name: game.name });
  }, [setReminderGame]);

  // Handler for "Compare" - opens Curate sidebar with comparison mode
  const handleCompare = useCallback((game) => {
    if (!game) return;

    // Close preview modal and open Curate sidebar
    setPreviewGame(null);
    setShowCurateSidebar(true);

    // Show comparison using new simplified interface
    // This will need a base game to compare against - for now, just open sidebar
    setTimeout(() => {
      curateBridge.resetCurate();
      showBanner?.('Select a base game first, then use Compare button on similar games', 'info');
    }, 500);
  }, [setPreviewGame, setShowCurateSidebar, curateBridge, showBanner]);

  // Handler for "Let's Play"
  const handleLetSPlay = useCallback((game) => {
    if (!game) return;

    if (setLetSPlayGame && setShowLetSPlayModal) {
      setLetSPlayGame(game);
      setShowLetSPlayModal(true);
    }
  }, [setLetSPlayGame, setShowLetSPlayModal]);

  return {
    handleSearchSimilar,
    handleAddToMarketplace,
    handleAddWatch,
    handleAddReminder,
    handleCompare,
    handleLetSPlay,
  };
}
